import { Subject, takeUntil } from 'rxjs';
import {
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  AfterViewInit,
  Input,
  TemplateRef,
  effect,
  OnDestroy,
  Signal,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import videojs from 'video.js';
import { onVideoPlayerErrorLoaded, onVideoPlayerInitialized } from '../../../../services/annotation.service';
import { ProjectService } from '../../../../services/project.service';
import '../../../../services/videojs-plugins/videojs-download';
import '../../../../services/videojs-plugins/videojs-resolution-switcher';
import { AssetUploadService } from '../../services/asset-upload.service';
import { VideoResolution, VideoViewerService } from './video-viewer.service';
import { onComparerMediaPlayerAction } from '../../../../services/comments.service';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../../services/media-player-store.service';
import { AnnotationPluginEventService } from '../../services/annotation-plugin-event.service';
import { MediaPlayerFile } from '../../../../interfaces/media-player-store.interface';
import { FileUtilsService } from '../../../../services/file-utils.service';
import { ProjectFileService } from '../../../../services/project-file.service';
import { MediaType } from '../../../../models/media-player-types';
import { currentAppToken } from '../../../../auth/app-token';
@Component({
  selector: 'video-viewer',
  templateUrl: './video-viewer.component.html',
  styleUrl: './video-viewer.component.scss',
  standalone: false,
})
export class VideoViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() instanceId: string;
  @Input() assetListTemplate!: TemplateRef<any>;
  @Input() assetInfoTemplate!: TemplateRef<any>;
  @Input() width: string = '400';
  @Input() height: string = '300';
  @Input() miniPlayer: boolean = false;
  @Input() videoPreloadMethod: string | null = null;
  @Input() autoPlayVideo: boolean = false;
  @Input() previewContainerHeight: string | null = null;

  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly assetUploadService: AssetUploadService = inject(AssetUploadService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly videoViewerService: VideoViewerService = inject(VideoViewerService);
  private readonly annotationPluginEventService: AnnotationPluginEventService = inject(AnnotationPluginEventService);
  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly fileUtilsService: FileUtilsService = inject(FileUtilsService);
  private readonly projectFileService: ProjectFileService = inject(ProjectFileService);
  readonly MediaType = MediaType;
  mpStore: MediaPlayerStore | null = null;

  @ViewChild('videoEl') videoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoContainer') videoContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('loadingVideoContainerHidden') loadingVideoContainer!: ElementRef<HTMLDivElement>;

  // Direct signals from mpStore
  readonly mediaTypeSignal: Signal<string | null> = computed(() => this.mpStore?.mediaTypeSignal() ?? null);
  readonly itemSignal: Signal<MediaPlayerFile | null> = computed(() => this.mpStore?.itemSignal() ?? null);
  readonly projectIdSignal: Signal<string | null> = computed(() => this.mpStore?.projectIdSignal() ?? null);
  readonly sectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.sectionIdSignal() ?? null);
  readonly subsectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.subsectionIdSignal() ?? null);
  readonly typeIdSignal: Signal<string | null> = computed(() => this.mpStore?.typeIdSignal() ?? null);
  readonly annotationsSignal: Signal<unknown[]> = computed(() => this.mpStore?.annotationsSignal() ?? []);
  readonly projectParticipantsSignal: Signal<any[]> = computed(() => this.mpStore?.projectParticipantsSignal() ?? []);
  readonly isShareLinkPreviewSignal: Signal<boolean> = computed(
    () => this.mpStore?.isShareLinkPreviewSignal() ?? false
  );
  readonly isReleasePreviewSignal: Signal<boolean> = computed(() => this.mpStore?.isReleasePreviewSignal() ?? false);
  readonly parentElementContainerSignal: Signal<string | null> = computed(
    () => this.mpStore?.parentElementContainerSignal() ?? null
  );
  readonly enableResponsiveSignal: Signal<boolean> = computed(() => this.mpStore?.enableResponsiveSignal() ?? false);
  readonly enableAnnotationSignal: Signal<boolean> = computed(() => this.mpStore?.enableAnnotationSignal() ?? false);
  readonly hideAnnotationToolbarSignal: Signal<boolean> = computed(
    () => this.mpStore?.hideAnnotationToolbarSignal() ?? false
  );
  readonly isFullscreenSignal: Signal<boolean> = computed(() => this.mpStore?.isFullscreenSignal() ?? false);
  readonly isErrorSignal: Signal<boolean> = computed(() => this.mpStore?.isErrorSignal() ?? false);
  readonly portfolioPreviewSignal: Signal<boolean> = computed(() => this.mpStore?.portfolioPreviewSignal() ?? false);
  readonly playerSignal: Signal<any> = computed(() => this.mpStore?.playerSignal() ?? null);
  readonly videoViewerActionSignal: Signal<string | null> = computed(
    () => this.mpStore?.videoViewerActionSignal() ?? null
  );
  readonly userInfoSignal: Signal<unknown> = computed(() => this.mpStore?.userInfoSignal() ?? null);
  readonly hasEditPermissionSignal: Signal<boolean> = computed(() => this.mpStore?.hasEditPermissionSignal() ?? false);
  readonly videoPreloadMethodSignal: Signal<string | null> = computed(
    () => this.mpStore?.videoPreloadMethodSignal() ?? null
  );

  currentResolution: WritableSignal<string> = signal<string>('720');
  currentPlaybackRate: WritableSignal<number> = signal<number>(1);

  constructor() {
    effect(() => {
      const action: string | null = this.videoViewerActionSignal();
      if (action) {
        if (action === 'checkValidOriginal') {
          this.checkValidOriginal();
        }
        this.mpStore?.updateStore({ videoViewerAction: null });
      }
    });
  }

  ngOnInit(): void {
    this.mpStore = this.mpStoreService.getStore(this.instanceId);
    if (!this.mpStore) {
      throw new Error(`Failed to get media player store for instanceId: ${this.instanceId}`);
    }
  }

  ngAfterViewInit(): void {
    this.createPlaybackRateComponent();
    onComparerMediaPlayerAction.pipe(takeUntil(this.destroy$)).subscribe({
      next: (action: { action: string }) =>
        this.videoViewerService.handleComparerAction(action.action, this.videoPlayer.nativeElement, this.instanceId),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mpStore?.updateStore({ annotationPlugin: null });
    if (this.playerSignal()) {
      this.playerSignal()?.dispose();
      this.mpStore?.updateStore({ player: null });
    }
    if (this.mpStore.videoTypeCheckIntervalSignal()) {
      clearTimeout(this.mpStore.videoTypeCheckIntervalSignal());
    }
    if (this.mpStore.videoSourceCheckIntervalSignal()) {
      clearTimeout(this.mpStore.videoSourceCheckIntervalSignal());
    }
  }

  public onVideoPlayerLoaded(): void {
    this.mpStore?.updateStore({ isVideoPlayerLoaded: true });
  }

  private createPlaybackRateComponent(): void {
    const BasePlaybackRateMenu: any = videojs.getComponent('PlaybackRateMenuButton');

    if (!BasePlaybackRateMenu) {
      return;
    }

    const CustomPlaybackRateMenu: any = class extends BasePlaybackRateMenu {
      public handleClick(event: any): void {
        event.preventDefault();
        event.stopPropagation();
        const isOpen: boolean = this.el().classList.contains('vjs-menu-open');
        const { menu } = this;
        if (isOpen) {
          this.el().classList.remove('vjs-menu-open');
          if (menu?.hide) {
            menu.hide();
          }
        } else {
          this.el().classList.add('vjs-menu-open');
          if (menu?.show) {
            menu.show();
          }
        }
      }
    };

    videojs.registerComponent('customPlaybackRateMenu', CustomPlaybackRateMenu);
  }

  private checkValidOriginal(): void {
    if (!this.mpStore) return;
    const item: MediaPlayerFile | null = this.mpStore.itemSignal();
    if (!item) return;

    setTimeout(() => {
      const thumbnailUrl: string = item.signedURL + '?thumbnail=_SMALL';
      const img: any = new Image();

      img.onload = (): void => {
        if (this.videoContainer) {
          this.videoContainer.nativeElement.classList.remove('hidden-container');
        }
        if (this.loadingVideoContainer) {
          this.loadingVideoContainer.nativeElement.classList.add('hidden-container');
        }
        this.initializeVideoJs();
      };

      img.onerror = (): void => {
        if (item.name && this.fileUtilsService.isFileExtension(item.name, 'mp4')) {
          if (this.videoContainer) {
            this.videoContainer.nativeElement.classList.remove('hidden-container');
          }
          if (this.loadingVideoContainer) {
            this.loadingVideoContainer.nativeElement.classList.add('hidden-container');
          }
          this.initializeVideoJs();
        } else {
          if (this.videoContainer) {
            this.videoContainer.nativeElement.classList.add('hidden-container');
          }
          if (this.loadingVideoContainer) {
            this.loadingVideoContainer.nativeElement.classList.remove('hidden-container');
          }

          if (item.signedURL) {
            fetch(item.signedURL + '?thumbnail=_TRANSCODED', {
              headers: {
                Authorization: 'Bearer ' + currentAppToken(),
              },
            });
          }
          const timeoutId: any = setTimeout(() => this.checkValidOriginal(), 5000);
          this.mpStore?.updateStore({ videoTypeCheckInterval: timeoutId });
        }
      };

      img.src = thumbnailUrl;
    }, 100);
  }

  private initializeVideoJs(): void {
    this.assetUploadService.cancelMetadataCheck();
    const checkAndInitialize: any = (): void => {
      if (!this.mpStore) return;
      const item: MediaPlayerFile | null = this.mpStore.itemSignal();
      if (!item) {
        return;
      }

      if (!item.metadata || JSON.parse(item.metadata).length === 0) {
        // Fetch updated file info
        const projectId: string = this.mpStore.projectIdSignal() ?? this.projectService.project?.id;
        if (!projectId) {
          // No project ID available, retry after interval
          this.mpStore?.updateStore({ isError: true });
          const timeoutId: any = setTimeout(checkAndInitialize, 5000);
          this.mpStore?.updateStore({ videoSourceCheckInterval: timeoutId });
          return;
        }

        this.projectFileService.getFileInfo(projectId, item.id).subscribe({
          next: (data: any) => {
            // Only update specific properties using helper service
            this.fileUtilsService.copyFilePropsAfterScanning(data, item);

            if (!item.metadata || JSON.parse(item.metadata).length === 0) {
              this.mpStore?.updateStore({ isError: true });
              const timeoutId: any = setTimeout(checkAndInitialize, 5000);
              this.mpStore?.updateStore({ videoSourceCheckInterval: timeoutId });
              return;
            }

            this.mpStore?.updateStore({ isError: false });
            this.initializePlayer();
          },
          error: () => {
            this.mpStore?.updateStore({ isError: true });
            const timeoutId: any = setTimeout(checkAndInitialize, 5000);
            this.mpStore?.updateStore({ videoSourceCheckInterval: timeoutId });
          },
        });
        return;
      }
      this.mpStore?.updateStore({ isError: false });
      this.initializePlayer();
    };
    checkAndInitialize();
  }

  private initializePlayer(): void {
    if (!this.mpStore) return;
    const item: MediaPlayerFile | null = this.mpStore.itemSignal();

    if (!item || this.mpStore.mediaTypeSignal() !== MediaType.VIDEO) return;

    if (this.mpStore.enableAnnotationSignal()) {
      this.annotationPluginEventService.initializeVideoAnnotations(this.instanceId);
    } else {
      this.initializePlainVideo();
    }
  }

  private initializePlainVideo(): void {
    setTimeout(() => {
      if (!this.mpStore) return;
      if (this.videoPlayer) {
        this.videoViewerService.setDefaultVideoOptions(
          this.width,
          this.height,
          this.autoPlayVideo,
          this.miniPlayer,
          this.instanceId
        );
        const player: any = videojs(this.videoPlayer.nativeElement, this.mpStore.videoOptionsSignal(), () => {
          this.videoViewerService.setVideoSource(true, this.instanceId, player);
          player.on('loadeddata', () => {
            this.mpStore?.updateStore({ isVideoPlayerLoaded: true });
            if (this.mpStore.portfolioPreviewSignal() && player.controlBar?.resolutionSwitcher) {
              player.controlBar.resolutionSwitcher.hide?.();
            }
            player.currentTime(0);
            this.videoViewerService.reorderControlBarButtons(player);
          });

          player.on('ready', () => {
            this.videoViewerService.reorderControlBarButtons(player);
            this.mpStore?.updateStore({ player: player });
            onVideoPlayerInitialized.emit({ act: 'videoPlayerInitialized' });
          });

          player.on('error', () => {
            onVideoPlayerErrorLoaded.emit();
            if (!this.mpStore.isVideoSourceSetSignal()) {
              this.mpStore?.updateStore({ isVideoSourceSet: false });
            }
          });
        });
      }
      this.mpStore?.updateStore({ isBusy: false });
    }, 0);
  }

  public onPlaybackRateChange(playbackRate: number): void {
    this.currentPlaybackRate.set(playbackRate);
    const player: any = this.playerSignal();
    if (player?.playbackRate) {
      player.playbackRate(playbackRate);
    }
  }

  public onResolutionChange(resolution: VideoResolution): void {
    this.currentResolution.set(resolution.res);
    const player: any = this.playerSignal();
    if (!player?.src) return;
    const currentTime: number = player.currentTime() ?? 0;
    const isPaused: boolean = player.paused();
    const playbackRate: number = player.playbackRate() ?? 1;
    player.pause();
    player.src([{ src: resolution.src, type: resolution.type, res: resolution.res }]);
    player.one('loadeddata', () => {
      player.currentTime(currentTime);
      player.playbackRate(playbackRate);
      if (!isPaused) {
        player.play();
      }
    });
  }
}
